import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BadgeCheck, Camera, Check, Loader2, TriangleAlert, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CameraCaptureDialog } from './CameraCaptureDialog';
import { OcrScanningIndicator } from './OcrScanningIndicator';
import { scanDocument } from '@/api/document.api';
import { useCartStore } from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';
import type { DocumentScanResult, DocumentType } from '@/types/sale';

const DOC_TYPES: Array<{ value: DocumentType; label: string }> = [
  { value: 'KTP', label: 'KTP' },
  { value: 'SIM', label: 'SIM' },
  { value: 'STNK', label: 'STNK' },
];

/** Fields carrying the document's primary id — masked in the readout (PII). */
const SENSITIVE_KEYS = new Set([
  'nik', 'no_ktp', 'nomor_ktp', 'id_number',
  'no_sim', 'nomor_sim', 'sim_number',
  'nopol', 'no_polisi', 'plat', 'plate', 'plate_number', 'nomor_polisi',
]);

const MAX_BYTES = 8 * 1024 * 1024; // mirror the server's multer limit

/** "tempat_lahir" → "Tempat lahir" */
function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Mask a primary-id value to its last 4 chars — never show the full id. */
function maskValue(value: string): string {
  const s = String(value ?? '');
  if (s.length <= 4) return s ? '•'.repeat(s.length) : '';
  return `${'•'.repeat(Math.max(0, s.length - 4))}${s.slice(-4)}`;
}

export function DocumentScanField({ requiredType }: { requiredType?: DocumentType | null }) {
  const { documentScan, setPromo } = useCartStore(
    useShallow((s) => ({ documentScan: s.promo.documentScan, setPromo: s.setPromo })),
  );
  // When the promo requires a specific document (KTP/SIM/STNK), that type is
  // authoritative: auto-select it and lock the picker so the cashier can't scan
  // the wrong kind (which would OCR with the wrong engine and never match).
  const [localType, setLocalType] = useState<DocumentType>(requiredType ?? 'KTP');
  const docType: DocumentType = requiredType ?? localType;
  const locked = Boolean(requiredType);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (file: File) => scanDocument(file, docType),
    onSuccess: (result: DocumentScanResult) => {
      setPromo({ documentScan: result, documentScanToken: result.scanToken });
      if (result.ocrVerified) {
        toast.success(`Dokumen ${result.documentType} terbaca.`);
      } else {
        toast.warning('OCR tidak menemukan data. Coba foto ulang yang lebih jelas.');
      }
    },
    onError: (err: { message?: string }) =>
      toast.error(err?.message ?? 'Pemindaian dokumen gagal.'),
  });

  /** Validate (type + size) then scan — shared by upload and camera capture. */
  const submitFile = (file: File) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Hanya gambar JPEG atau PNG yang didukung.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Ukuran gambar maksimal 8 MB.');
      return;
    }
    mutate(file);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-picking the SAME file still fires onChange.
    e.target.value = '';
    if (file) submitFile(file);
  };

  const clear = () => setPromo({ documentScan: null, documentScanToken: null });

  const cameraDialog = (
    <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen} onCapture={submitFile} />
  );

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png"
      capture="environment"
      className="sr-only"
      onChange={onPickFile}
    />
  );

  // ── Scanned: show the trusted extraction for cashier confirmation. ──────
  if (documentScan) {
    const entries = Object.entries(documentScan.fields ?? {});
    return (
      <div className="space-y-3 rounded-md border-2 border-success/40 bg-success/5 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-success/15 text-success">
              <Check className="h-4 w-4" />
            </span>
            <div>
              <p className="font-semibold text-ink-900">
                {documentScan.documentType} terpindai
              </p>
              {documentScan.primaryIdMasked && (
                <p className="font-mono text-xs text-ink-400">{documentScan.primaryIdMasked}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={clear}
            aria-label="Hapus hasil pindai"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {!documentScan.ocrVerified && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
            <TriangleAlert className="h-3.5 w-3.5" />
            OCR tidak menemukan data — foto ulang jika promo tidak muncul.
          </p>
        )}

        {entries.length > 0 && (
          <dl className="grid gap-x-4 gap-y-1.5 rounded-md border border-border bg-card p-3 sm:grid-cols-2">
            {entries.map(([key, value]) => (
              <div key={key} className="flex justify-between gap-2 text-xs">
                <dt className="shrink-0 text-ink-400">{humanizeKey(key)}</dt>
                <dd className="truncate text-right font-medium text-ink-900">
                  {SENSITIVE_KEYS.has(key.toLowerCase()) ? maskValue(value) : value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => setCameraOpen(true)}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Camera className="mr-1.5 h-4 w-4" /> Kamera
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending}
          >
            <Upload className="mr-1.5 h-4 w-4" /> Unggah
          </Button>
        </div>
        {hiddenInput}
        {cameraDialog}
      </div>
    );
  }

  // ── Idle: choose a document type, then scan (camera or upload). ──────────
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="doc-type">Jenis dokumen</Label>
        <Select
          value={docType}
          onValueChange={(v) => setLocalType(v as DocumentType)}
          disabled={locked}
        >
          <SelectTrigger id="doc-type" aria-label="Jenis dokumen" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOC_TYPES.map((d) => (
              <SelectItem key={d.value} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {locked && (
          <p className="text-xs text-ink-400">
            Promo ini memerlukan dokumen {docType}.
          </p>
        )}
      </div>
      {isPending ? (
        <OcrScanningIndicator />
      ) : (
        <>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setCameraOpen(true)}
            >
              <Camera className="mr-1.5 h-4 w-4" /> Kamera
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-1.5 h-4 w-4" /> Unggah
            </Button>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-ink-400">
            <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
            Server membaca dokumen (OCR) untuk membuka promo berbasis identitas. Nomor identitas
            disamarkan.
          </p>
        </>
      )}
      {hiddenInput}
      {cameraDialog}
    </div>
  );
}
